import React, { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "../css/ChatPage.css";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import crossIcon from "../assets/UserIcons/crossIcon.svg";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import cpyIcon from "../assets/UserIcons/Copy.svg";
import speakerIcon from "../assets/UserIcons/Speaker.svg";
import { branding } from "../utils/branding";

const ViewConversation = () => {
  const navigate = useNavigate();
  const { session_id } = useParams();
  const [messages, setMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [userName, setUserName] = useState("User");
  const [userPic, setUserPic] = useState(null);
  const [firstMessageTime, setFirstMessageTime] = useState(null);
  const chatEndRef = useRef(null);

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  useEffect(() => {
    const fetchSessionMessages = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/chat/session1/${session_id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch session messages");
        }

        const data = await response.json();
        const {
          sessionMessages,
          user_name,
          user_profile_pic,
          first_message_time,
        } = data.data || {};

        if (!sessionMessages) {
          console.error("No sessionMessages found in response");
          return;
        }

        const formatted = sessionMessages.flatMap((row, idx) => [
          {
            id: idx * 2,
            sender: "user",
            message: row.user_input,
            time: new Date(row.createdAt).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
            userName: user_name || "User",
            userPic: user_profile_pic,
          },
          {
            id: idx * 2 + 1,
            sender: "bot",
            message: row.bot_response,
            time: new Date(row.createdAt).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          },
        ]);

        setMessages(formatted);
        setUserName(user_name || "User");
        setUserPic(user_profile_pic);
        setFirstMessageTime(first_message_time);
      } catch (err) {
        console.error("Error loading session chat:", err);
      }
    };

    if (session_id) fetchSessionMessages();
  }, [session_id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleCopy = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        showSuccessToast("Text copied to clipboard!");
      } else {
        // ✅ Fallback for older browsers / Safari / HTTP
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        showSuccessToast("Text copied to clipboard!");
      }
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  let currentUtterance = null;

  const handleTextToSpeech = (text) => {
    // If already speaking -> stop it first
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
      // If same message clicked again → stop sound
      if (currentUtterance && currentUtterance.text === text) {
        currentUtterance = null;
        return;
      }
    }

    // Create new utterance
    const utterance = new SpeechSynthesisUtterance(text);
    currentUtterance = utterance;

    // Optional: Set voice, pitch, rate, etc.
    utterance.lang = "en-US";
    utterance.rate = 1;
    utterance.pitch = 1;

    // When finished → reset current utterance
    utterance.onend = () => {
      currentUtterance = null;
    };

    // Speak
    window.speechSynthesis.speak(utterance);
  };

  const renderMessage = (msg) => {
    const isBot = msg.sender === "bot";
    const profile = isBot
      ? "/img/botIcon.svg"
      : msg.userPic ||
        "https://cdn-icons-png.flaticon.com/512/3135/3135715.png";

    const bubbleClass = isBot ? "bot-bubble" : "user-bubble";
    const containerAlign = isBot ? "flex-row" : "flex-row-reverse";
    const iconAlign = isBot ? "justify-content-start" : "justify-content-end";
    const iconMargin = isBot ? "ms-3" : "me-3";

    return (
      <div
        className={`d-flex ${containerAlign} align-items-start my-3`}
        key={msg.id}
      >
        <div className="d-flex flex-column" style={{ maxWidth: "75%" }}>
          <div className={`chat-bubble ${bubbleClass}`}>
            <div className="d-flex align-items-center gap-2 mb-2">
              <img
                src={profile}
                className={isBot ? "bot-avatar" : "chat-avatar"}
                alt="avatar"
              />
              <strong>
                {isBot ? branding("ZeroBox") : msg.userName || "User"}
              </strong>
              <small className="">{msg.time}</small>
            </div>

            <div className="formatted-text">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  code({ node, inline, className, children, ...props }) {
                    const match = /language-(\w+)/.exec(className || "");
                    return !inline && match ? (
                      <SyntaxHighlighter
                        style={oneDark}
                        language={match[1]}
                        PreTag="div"
                        customStyle={{
                          borderRadius: "10px",
                          padding: "1rem",
                        }}
                        {...props}
                      >
                        {String(children).replace(/\n$/, "")}
                      </SyntaxHighlighter>
                    ) : (
                      <code
                        className="bg-light px-2 py-1 rounded"
                        style={{ fontFamily: "monospace" }}
                        {...props}
                      >
                        {children}
                      </code>
                    );
                  },
                }}
              >
                {msg.message}
              </ReactMarkdown>
            </div>
          </div>

          <div
            className={`d-flex align-items-center text-secondary align-items-center gap-3 mt-1 ${iconAlign} ${iconMargin}`}
          >
            {isBot && (
              <>
                <img
                  src={speakerIcon}
                  alt="Play Sound"
                  className="icon-image"
                  role="button"
                  title={
                    window.speechSynthesis.speaking &&
                    currentUtterance?.text === msg.message
                      ? "Stop Sound"
                      : "Play Sound"
                  }
                  onClick={() => handleTextToSpeech(msg.message)}
                  style={{
                    width: "16px",
                    height: "16px",
                    cursor: "pointer",
                    opacity:
                      window.speechSynthesis.speaking &&
                      currentUtterance?.text === msg.message
                        ? 0.6 // Slight fade to indicate “active”
                        : 1,
                    transition: "opacity 0.3s ease",
                  }}
                />
                <img
                  src={cpyIcon}
                  className="icon-image  "
                  alt="Copy"
                  role="button"
                  title="Copy"
                  onClick={() => handleCopy(msg.message)}
                  style={{ width: "16px", height: "16px", cursor: "pointer" }}
                />
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const filteredMessages = messages.filter((msg) =>
    msg.message?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="vh-100 d-flex flex-column ">
      <img
        src={crossIcon}
        alt="Close"
        className="custom-close-icon icon-image"
        role="button"
        onClick={() => navigate(-1)}
        style={{ width: "24px", height: "24px", cursor: "pointer" }}
      />

      <h5 className="mb-0 fw-bold refresh-prompts text-center mt-5">
        View Conversation
      </h5>

      {/* Search */}
      <div className="d-flex justify-content-end align-items-center px-3 py-2 border-bottom">
        <div className="custom-search-wrapper w-25 mt-3">
          <div className="input-group custom-search-group">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search chat content"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-grow-1 overflow-y-auto chat-box">
        {filteredMessages.length > 0 ? (
          filteredMessages.map((msg) => renderMessage(msg))
        ) : (
          <div className="text-center refresh-prompts  py-4">
            No messages found.
          </div>
        )}
        <div ref={chatEndRef} />
      </div>
    </div>
  );
};

export default ViewConversation;
